import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'components/DataTable';
import qs from 'query-string';
import { DATE_FORMATS, formatDate, getPageLimit, showError, showSuccess, showWarning, updatePageLimit } from 'utils';
import { tableStyles } from '../../../css/tableStyles';
import styles from './index.module.scss';
import { deletePickPackList, fetchPickPackList, fetchPickPackListDetail, generateFormattedDataForPickPackList, togglePickPackListLock } from 'services';
import RowActionComponent from 'components/RowActionComponent';
import ActionButton from 'components/ActionButton';
import PickPackPDF from 'components/PickPackPDF';
import { IPickPackList } from 'interfaces/pickPackList';
import Filter from './Filter';
import CustomLink from 'components/TableLink';

let filters: { [p: string]: any; page: number } = {
  page: 0,
};

const PickPacks = props => {
  const [loading, setLoading] = useState<boolean>(false);
  const [pickPacks, setPickPacks] = useState<Record<string, any>>({});
  const [pdfDataAvailable, setPDFDataAvailable] = useState<boolean>(false);
  const [pdfData, setPDFData] = useState<IPickPackList.IPickPackPDFData>();

  const fetchPickPacksFn = async () => {
    setLoading(true);
    const { page, ...rest } = filters;
    try {
      const response = await fetchPickPackList({
        page,
        limit: getPageLimit(),
        filter: {
          ...rest,
        },
      });
      setPickPacks(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const queryStringToFilter = () => {
    return qs.parse(props.location.search, { parseBooleans: true, parseNumbers: true });
  };

  useEffect(() => {
    filters = {
      ...filters,
      ...queryStringToFilter(),
    };
    fetchPickPacksFn();
  }, []);

  const generatePDF = async (id: string) => {
    try {
      const response = await generateFormattedDataForPickPackList(id);
      showSuccess(response);
      fetchPickPacksFn();
    } catch (e) {
      showError(e);
    }
  };

  const deletePickPackListFn = async (id: string) => {
    try {
      await deletePickPackList(id);
      fetchPickPacksFn();
    } catch (e) {
      showError(e);
    }
  };

  const togglePickPackLock = async (id: string) => {
    try {
      await togglePickPackListLock(id);
      fetchPickPacksFn();
    } catch (e) {
      showError(e);
    }
  };

  const getPDFDataForPickPack = async (id: string) => {
    const response = await fetchPickPackListDetail(id);
    setPDFDataAvailable(true);
    setPDFData(response.pickPackList.formattedData);
    showWarning('The PDF is being prepared, please wait for a few seconds.');
    setTimeout(window.print, 10000);
  };

  const columns = useMemo(() => [
    {
      name: 'Pick Pack ID',
      selector: 'objectId',
      sortable: false,
      // eslint-disable-next-line react/display-name
      cell: row => (
        <CustomLink
          text={row.objectId}
          linkTo={`/pick-packs/${row.objectId}`}
        />
      ),
    },
    {
      name: 'Name',
      selector: 'name',
      sortable: true,
      // eslint-disable-next-line react/display-name
      format: (row) => (
        <div className={styles['name']} onClick={() => window.location.assign(`/pick-packs/${row.objectId}`)}>
          <div>{row.name}</div>
          {row.pdfDataGenerationInProgress ? (
            <div className={styles['generation-message']}>
              PDF Data Generation In Progress
            </div>
          ) : null}
        </div>
      ),
    },
    {
      name: 'Created At',
      selector: 'createdAt',
      sortable: true,
      format: (row) => formatDate(row.createdAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A),
    },
    {
      name: 'Pick Pack List No',
      selector: 'pickPackListNo',
      sortable: true,
    },
    {
      name: 'Locked',
      selector: 'locked',
      sortable: true,
      width: '120px',
      format: (row) => row.locked?.toString(),
    },
    {
      name: '',
      width: '80px',
      // eslint-disable-next-line react/display-name
      cell: (row) => (
        <RowActionComponent
          actions={[
            {
              text: 'Toggle Lock',
              onClick: () => togglePickPackLock(row.objectId),
            },
            {
              text: row.hasFormattedData ? 'Print PDF' : 'Generate PDF Data',
              onClick: () => row.hasFormattedData
                ? getPDFDataForPickPack(row.objectId)
                : generatePDF(row.objectId),
            },
            {
              text: 'Delete',
              onClick: () => deletePickPackListFn(row.objectId),
              customClass: styles['delete'],
            },
          ]}
        />
      ),
    },
  ], []);

  const filterToQueryString = () => {
    return qs.stringify({ ...filters });
  };

  const updateQueryString = () => {
    const qs = filterToQueryString();
    props.history.push({
      pathname: props.location.pathname,
      search: qs,
    });
  };

  const onChangePage = (page: number) => {
    filters.page = page - 1;
    updateQueryString();
    fetchPickPacksFn();
  };

  const onChangeRowsPerPage = (rowsPerPage: number, currentPage: number) => {
    filters.limit = rowsPerPage;
    updatePageLimit(rowsPerPage);
    onChangePage(currentPage);
  };

  const onFilterSubmit = (updatedFilter: Record<string, any>) => {
    filters = { ...updatedFilter, page: 0 };
    updateQueryString();
    fetchPickPacksFn();
  };

  return (
    <div>
      <div className={styles['main-container']}>
        <div className={styles['header']}>
          <div className={styles['heading']}>Pick Packs</div>
          <div className={styles['buttons-container']}>
            <ActionButton
              text={<>Pick Pack <span>+</span></>}
              active={true}
              isLoading={false}
              onClick={() => props.history.push('/pick-packs/create')}
              customClass={styles['add-new-pick-pack-button']}
            />
          </div>
        </div>
        <Filter {...props} onSubmit={onFilterSubmit} />
        <DataTable
          keyField="objectId"
          columns={columns}
          noHeader={true}
          data={pickPacks.items}
          highlightOnHover={true}
          customStyles={tableStyles}
          pagination={true}
          paginationServer={true}
          paginationPerPage={getPageLimit()}
          paginationDefaultPage={(filters.page || 0) + 1}
          paginationTotalRows={pickPacks.total}
          paginationRowsPerPageOptions={[50, 100, 200]}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          loading={loading}
        />
      </div>
      {(!!pdfData && pdfDataAvailable) ? (
        <div id={'pdf-preview'} className={styles['pdf-preview']}>
          <PickPackPDF pdfData={pdfData} />
        </div>
      ) : null}
    </div>
  );
};

export { PickPacks };
